import {
  AsYouType,
  type CountryCode as Code,
  formatIncompletePhoneNumber,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  parsePhoneNumberWithError,
  validatePhoneNumberLength,
} from 'libphonenumber-js'
import examples from 'libphonenumber-js/examples.mobile.json'

export type CountryCode = Lowercase<Code>

function formatPhoneGroups(value: string): string {
  const separated = value
    .replace(/[\s()-]+/g, '-')
    .replace(/^-|-$/g, '')
    .replace(/^(\+\d+)-/, '$1 ')

  const grouped = separated.replace(/(\d{3})-(\d{2})(\d{2})$/, '$1-$2-$3')

  return grouped.startsWith('+') ? grouped.replace(/^(\+\d+ \d+)-/, '$1 ') : grouped.replace(/^(\d+)-/, '$1 ')
}

export const isValidPhone = (phone: string, defaultCode: CountryCode = 'ru') => {
  return isValidPhoneNumber(phone, defaultCode.toUpperCase() as Code)
}

const PRIORITY_COUNTRIES: Code[] = ['RU', 'KZ', 'TH', 'TR', 'IN']

export const normalizePhone = (phone?: string | null, defaultCode?: CountryCode) => {
  if (!phone) return ''

  // Предобработка: определяем страну по префиксу и длине номера
  const digits = phone.replace(/\D/g, '')

  // RU/KZ: 10 цифр, мобильные начинаются с 7 (KZ) или 9 (RU)
  if (digits.length === 10 && (digits.startsWith('9') || digits.startsWith('7'))) {
    phone = `+7${digits}`
  }
  // TH: 9 цифр, мобильные начинаются с 6, 8, 9
  else if (digits.length === 9 && /^[689]/.test(digits)) {
    phone = `+66${digits}`
  }
  // TR: 10 цифр, мобильные начинаются с 5
  else if (digits.length === 10 && digits.startsWith('5')) {
    phone = `+90${digits}`
  }
  // IN: 10 цифр, мобильные начинаются с 6, 7, 8, 9
  else if (digits.length === 10 && /^[6789]/.test(digits)) {
    phone = `+91${digits}`
  }

  // Попытка 1: Парсинг с указанной страной (если передана)
  if (defaultCode) {
    try {
      const parsed = parsePhoneNumberWithError(phone, { defaultCountry: defaultCode.toUpperCase() as Code })
      return parsed.number
    } catch {
      console.warn(`Failed to parse phone with default country: ${defaultCode}`)
    }
  }

  // Попытка 2: Авто-детект (если номер содержит код страны)
  try {
    const parsed = parsePhoneNumberWithError(phone)
    return parsed.number
  } catch {
    // console.warn('Failed to auto-detect phone number country')
  }

  // Попытка 3: Добавляем + и пробуем авто-детект
  if (!phone.startsWith('+')) {
    try {
      const parsed = parsePhoneNumberWithError(`+${phone}`)
      return parsed.number
    } catch {
      console.warn('Failed to auto-detect phone number country with + prefix')
    }
  }

  // Попытка 4: Перебираем приоритетные страны
  for (const country of PRIORITY_COUNTRIES) {
    try {
      const parsed = parsePhoneNumberWithError(phone, { defaultCountry: country })
      return parsed.number
    } catch {
      console.warn(`Failed to parse phone with priority country: ${country}`)
    }
  }

  // Если ничего не сработало, возвращаем оригинал
  return phone
}

export const getPhoneDigits = (phone: string, slice = 1) => {
  return phone.replace(/\D/g, '').slice(slice)
}

export function capPhoneDigits(digits: string, hasKg: boolean): string {
  if (digits.startsWith('7') || digits.startsWith('8')) return digits.slice(0, 11)

  if (digits.startsWith('9')) {
    const mayBeKgInternational = hasKg && '996'.startsWith(digits.slice(0, Math.min(digits.length, 3)))

    return digits.slice(0, mayBeKgInternational ? 12 : 10)
  }

  return digits.slice(0, 15)
}

export function isPhoneFormatSnap(previous: string, next: string): boolean {
  const previousDigits = getPhoneDigits(previous, 0)
  const nextDigits = getPhoneDigits(next, 0)
  const comparablePrevious = previousDigits.replace(/^8/, '7')

  return (
    previousDigits.length >= 10 &&
    previous !== next &&
    !/[\s-]/.test(previous) &&
    /[\s-]/.test(next) &&
    (nextDigits === comparablePrevious || nextDigits.endsWith(comparablePrevious))
  )
}

// Отсекаем лишние цифры только при заведомо слишком длинном номере. Между
// допустимыми длинами может быть промежуточный `INVALID_LENGTH` (например,
// у GB и CA после семи цифр), который не должен блокировать дальнейший ввод.
function capNationalDigits(digits: string, country: Code): string {
  let accepted = ''

  for (const digit of digits) {
    const candidate = accepted + digit
    const candidateLength = validatePhoneNumberLength(candidate, country)

    if (candidateLength === 'TOO_LONG') break

    accepted = candidate
  }

  return accepted
}

/**
 * Форматирует НАЦИОНАЛЬНУЮ часть номера «по мере ввода» под правила конкретной
 * страны и ограничивает длину максимально возможной для этой страны.
 *
 * Разделители расставляет libphonenumber `AsYouType`. Часть стран (BY, AM, AZ,
 * KG, MD, TM…) не форматируются, если скормить только национальный номер, —
 * `AsYouType` применяет шаблон лишь в международном контексте. Поэтому: сначала
 * пробуем национальный формат (RU получает группировку `999-888-77-66`),
 * а если разделители не появились — форматируем международный номер
 * (`+КОД` + национальный) и срезаем код страны, оставляя национальную часть.
 *
 * Возвращает строку с дефисами между группами цифр. Для валидации/отправки разделители
 * снимаются штатно через `getPhoneDigits` / `normalizePhone`.
 */
export function formatNationalPhoneInput(input: string, countryCode: CountryCode): string {
  const digits = getPhoneDigits(input, 0)

  if (!digits) return ''

  const country = countryCode.toUpperCase() as Code

  // Страны без телефонных метаданных (напр. UM) libphonenumber не форматирует, а
  // getCountryCallingCode для них кидает. Отдаём сырые цифры без форматирования.
  if (!isSupportedCountry(country)) return digits

  const accepted = capNationalDigits(digits, country)

  const national = new AsYouType(country).input(accepted)

  // Национальный формат сработал (есть разделители) — оставляем как есть.
  if (/[\s\-()]/.test(national)) return formatPhoneGroups(national)

  // Иначе форматируем через международный номер и срезаем код страны.
  const callingCode = getCountryCallingCode(country)
  const international = new AsYouType(country).input(`+${callingCode}${accepted}`).replace(/^\+/, '')
  const withoutCode = international.startsWith(callingCode) ? international.slice(callingCode.length) : international

  return formatPhoneGroups(withoutCode.replace(/^[\s\-()]+/, '') || national)
}

export function formatSeparatedNationalPhoneInput(value: string, countryCode: CountryCode): string {
  return formatNationalPhoneInput(value, countryCode)
}

export function getInternationalPhoneFromNationalInput(input: string, countryCode: CountryCode): string {
  const digits = getPhoneDigits(input, 0)

  if (!digits) return ''

  const country = countryCode.toUpperCase() as Code

  if (!isSupportedCountry(country)) return input

  const formatter = new AsYouType(country)
  formatter.input(input)

  return formatter.getNumber()?.number ?? `${getPhoneCallingPrefix(countryCode)}${digits}`
}

/**
 * Плейсхолдер-пример национального номера для страны — реальный пример из
 * метаданных libphonenumber, отформатированный тем же `formatNationalPhoneInput`,
 * чтобы вид плейсхолдера совпадал с тем, что получит пользователь при вводе.
 * Возвращает '' если для страны нет примера.
 */
export function getPhonePlaceholder(countryCode: CountryCode): string {
  const example = getExampleNumber(countryCode.toUpperCase() as Code, examples)

  if (!example) return ''

  return formatNationalPhoneInput(example.nationalNumber, countryCode)
}

// Не российский номер (BY, KZ, AM…): форматируем международно по метаданным libphonenumber.
// Только на полностью валидном номере — иначе поле ввода перекраивало бы номер по ходу набора
function formatInternationalIfValid(phone: string): string | undefined {
  const digits = getPhoneDigits(phone, 0)

  if (!digits) return undefined

  const parsed = parsePhoneNumberFromString(phone.startsWith('+') ? phone : `+${digits}`)

  return parsed?.isValid() ? formatPhoneGroups(parsed.formatInternational()) : undefined
}

export function formatPhoneDisplay(phone: string): string {
  if (!phone.trim()) return ''

  const normalized = normalizePhone(phone)
  const parsed = parsePhoneNumberFromString(normalized)

  return parsed ? formatPhoneGroups(parsed.formatInternational()) : formatPhoneNumber(normalized)
}

export function formatPhoneNumber(
  phone: string,
  options: {
    isReplace?: boolean
    isInternational?: boolean
    country?: Code
  } = {},
): string {
  const { isReplace = true, isInternational = false, country = 'RU' } = options

  // Форматируем международный номер
  if (isInternational) {
    const parsed = parsePhoneNumberFromString(phone, country)
    return parsed ? formatPhoneGroups(parsed.formatInternational()) : ''
  }

  let digits = getPhoneDigits(phone, 0)

  if (!digits.length && isReplace) {
    return '+7'
  }

  if (phone.startsWith('+7+')) {
    digits = digits.substring(1)
  }

  if (digits.startsWith('779') || digits.startsWith('789')) {
    digits = `79${digits.substring(3)}`
  }

  if (digits.length > 11) {
    return formatInternationalIfValid(phone) ?? phone
  }

  const normalized = digits.startsWith('8') ? `7${digits.slice(1)}` : digits

  if (!normalized.startsWith('7')) {
    return formatInternationalIfValid(phone) ?? phone
  }

  return formatPhoneGroups(formatIncompletePhoneNumber(`+${normalized}`, country))
}

// Текущий международный префикс страны +7 +86
export function getPhoneCallingPrefix(countryCode: CountryCode): string {
  const country = countryCode.toUpperCase() as Code

  if (!isSupportedCountry(country)) return '+'

  return `+${getCountryCallingCode(country)}`
}

// Форматирует номер с кодом страны; пустое значение оставляет префикс
export function formatInternationalPhoneInput(input: string, countryCode: CountryCode): string {
  if (countryCode === 'ru') {
    return formatPhoneNumber(input)
  }

  const country = countryCode.toUpperCase() as Code

  if (!isSupportedCountry(country)) return input

  const callingCode = getCountryCallingCode(country)
  const digits = getPhoneDigits(input, 0)

  if (!digits.length) {
    return `+${callingCode}`
  }

  const national = digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits
  const accepted = capNationalDigits(national, country)

  return (
    formatPhoneGroups(formatIncompletePhoneNumber(`+${callingCode}${accepted}`, country)) ||
    `+${callingCode}${accepted}`
  )
}

// Международный ввод без известной страны: пустое значение — только «+».
export function formatLooseInternationalPhone(input: string): string {
  const digits = getPhoneDigits(input, 0).slice(0, 15)

  if (!digits.length) return '+'

  const formatter = new AsYouType()
  const formatted = formatter.input(`+${digits}`)

  return formatted.startsWith('+') ? formatPhoneGroups(formatted) : `+${digits}`
}

const KG_INTERNATIONAL_LENGTH = 12 // 996 + 9 цифр

// Киргизия: 996XXXXXXXXX показываем как +996 ….
export function formatKgPhoneInput(input: string): string {
  const digits = getPhoneDigits(input, 0)

  if (!digits) return ''

  return formatLooseInternationalPhone(digits.slice(0, KG_INTERNATIONAL_LENGTH))
}

// Страна по префиксу среди разрешённых. Если подходит несколько — берём более длинный код (+7 vs +86).
export function detectPhoneCountryByCallingCode(
  phone: string,
  allowedCountries: readonly string[] = ['RU', ...getCountries()],
): CountryCode | undefined {
  const digits = getPhoneDigits(phone, 0)

  if (!digits) return undefined

  let matched: CountryCode | undefined
  let matchedCallingCodeLength = 0

  for (const allowed of allowedCountries) {
    const country = allowed.toUpperCase() as Code

    if (!isSupportedCountry(country)) continue

    const callingCode = getCountryCallingCode(country)

    if (digits.startsWith(callingCode) && callingCode.length > matchedCallingCodeLength) {
      matched = allowed.toLowerCase() as CountryCode
      matchedCallingCodeLength = callingCode.length
    }
  }

  return matched
}

export function resolvePastedInternationalPhone(value: string, preferredCountries: readonly string[]) {
  const internationalStart = value.lastIndexOf('+')
  const rawDigits = getPhoneDigits(value, 0)
  let internationalValue = ''

  if (internationalStart !== -1) {
    internationalValue = value.slice(internationalStart)
  } else if (value === rawDigits && isValidPhone(`+${rawDigits}`)) {
    internationalValue = `+${rawDigits}`
  }

  if (!internationalValue) return null

  const digits = getPhoneDigits(internationalValue, 0)
  const detectedCountry = detectPhoneCountryByCallingCode(internationalValue)

  if (!detectedCountry) return null

  const international = `+${digits}`

  if (isValidPhone(international, detectedCountry)) return { country: detectedCountry, phone: international }

  // Если исходный код не даёт валидный номер, пробуем оставшиеся цифры с кодами доступных стран.
  const detectedPrefix = getPhoneDigits(getPhoneCallingPrefix(detectedCountry), 0)
  const national = digits.slice(detectedPrefix.length)

  for (const code of preferredCountries) {
    const country = code.toLowerCase() as CountryCode
    const candidate = `${getPhoneCallingPrefix(country)}${national}`

    if (isValidPhone(candidate, country) && parsePhoneNumberFromString(candidate)?.country?.toLowerCase() === country) {
      return { country, phone: candidate }
    }
  }

  return { country: detectedCountry, phone: international }
}
